import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@sentry/react';
import ProLayout from '../components/common/ProLayout';
import PublicLayout from '../components/common/PublicLayout';
import ScrollToTop from '../components/common/ScrollToTop';
import RegistrationRouteWrapper from '../components/application/RegistrationRouteWrapper';
import App from '../App';
import FallbackErrorPage from '../pages/FallbackErrorPage';
import LoadingPage from '../pages/LoadingPage';

// Route-level code splitting — each page is loaded only when its route is visited
const CustomerAccountCreatedPage = lazy(
  () => import('../pages/CustomerAccountCreatedPage')
);
const SubcontractorHomePage = lazy(
  () => import('../pages/SubcontractorHomePage')
);
const PricingPage = lazy(() => import('../pages/PricingPage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));
const SubmitInsurancePage = lazy(() => import('../pages/SubmitInsurancePage'));
const ProjectBoardPage = lazy(() => import('../pages/ProjectBoardPage'));
const ViewProjectBoardPage = lazy(
  () => import('../pages/ViewProjectBoardPage')
);
const VerifyCompanyInfoPage = lazy(
  () => import('../pages/VerifyCompanyInfoPage')
);
const VerifyCompanyInformationCompletedPage = lazy(
  () => import('../pages/VerifyCompanyInformationCompletedPage')
);
const ExpiredCompanyVerificationLinkPage = lazy(
  () => import('../pages/ExpiredCompanyVerificationLinkPage')
);
const SubcontractorCreateAccountPage = lazy(
  () => import('../pages/SubcontractorCreateAccountPage')
);
const HomePage = lazy(() => import('../pages/HomePage'));
const ContactUsPage = lazy(() => import('../pages/ContactUsPage'));
const SearchResults = lazy(() => import('../pages/SearchResultsPage'));
const RequestEstimatePage = lazy(() => import('../pages/RequestEstimatePage'));
const PrivacyPolicyPage = lazy(() => import('../pages/PrivacyPolicyPage'));
const TermsAndConditionsPage = lazy(
  () => import('../pages/TermsAndConditionsPage')
);
const UnsubscribePage = lazy(() => import('../pages/UnsubscribePage'));
const ContractorProfilePage = lazy(
  () => import('../pages/ContractorProfilePage')
);
const RequestEstimateSuccessPage = lazy(
  () => import('../pages/RequestEstimateSuccessPage')
);
const FAQsPage = lazy(() => import('../pages/FAQsPage'));
const TradesPage = lazy(() => import('../pages/TradesPage'));
const VendorListPage = lazy(() => import('../pages/VendorListPage'));
const EstimateReviewPage = lazy(() => import('../pages/EstimateReviewPage'));
const CustomerCreateAccountPage = lazy(
  () => import('../pages/CustomerCreateAccountPage')
);
const HelpCenterPage = lazy(() => import('../pages/HelpCenterPage'));
const OurServicesPage = lazy(() => import('../pages/OurServicesPage'));
const HelpCenterCustomersRoutes = lazy(
  () => import('./HelpCenterCustomersRoutes')
);
const HelpCenterContractorRoutes = lazy(
  () => import('./HelpCenterContractorRoutes')
);
const SignUpPage = lazy(() => import('@/pages/SignUpPage'));
const SubcontractorRegistrationPage = lazy(
  () => import('@/pages/SubcontractorRegistrationPage')
);
const SubcontractorAccountCreatedPage = lazy(
  () => import('@/pages/SubcontractorAccountCreatedPage')
);
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'));
const ValidateEmailPage = lazy(() => import('@/pages/ValidateEmailPage'));
const CreatePasswordPage = lazy(() => import('@/pages/CreatePasswordPage'));

const fallback = <LoadingPage />;

const S = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={fallback}>{children}</Suspense>
);

const proRoutes = {
  element: <ProLayout />,
  children: [
    {
      element: <ScrollToTop />,
      children: [
        {
          path: '/pro/verify-company-info/link-expired',
          element: (
            <S>
              <ExpiredCompanyVerificationLinkPage />
            </S>
          ),
        },
        {
          path: '/pro/pricing',
          element: (
            <S>
              <PricingPage />
            </S>
          ),
        },
        {
          path: '/pro/project-boards',
          element: (
            <S>
              <ProjectBoardPage />
            </S>
          ),
        },
        {
          path: '/pro/project-boards/:portfolio_id',
          element: (
            <S>
              <ViewProjectBoardPage />
            </S>
          ),
        },
        {
          path: '/pro/home',
          element: (
            <S>
              <SubcontractorHomePage />
            </S>
          ),
        },
        {
          path: '/pro/insurance/submit-coi/:token',
          element: (
            <S>
              <SubmitInsurancePage />
            </S>
          ),
        },
        {
          path: '/pro/*',
          element: (
            <S>
              <NotFoundPage />
            </S>
          ),
        },
      ],
    },
  ],
};

const publicRoutes = {
  element: <PublicLayout />,
  children: [
    {
      path: '/faqs',
      element: (
        <S>
          <FAQsPage />
        </S>
      ),
    },
    {
      element: <ScrollToTop />,
      children: [
        {
          path: '/customers/search',
          element: (
            <S>
              <SearchResults />
            </S>
          ),
        },
        {
          path: '/contractor/profile/:id/:slug?',
          element: (
            <S>
              <ContractorProfilePage />
            </S>
          ),
        },
        { path: '/', element: <Navigate to='/home' /> },
        {
          path: '/home',
          element: (
            <S>
              <HomePage />
            </S>
          ),
        },
        {
          path: '/our-services',
          element: (
            <S>
              <OurServicesPage />
            </S>
          ),
        },
        {
          path: '/contact-us',
          element: (
            <S>
              <ContactUsPage />
            </S>
          ),
        },
        {
          path: '/help-center',
          element: (
            <S>
              <HelpCenterPage />
            </S>
          ),
        },
        {
          path: '/help-center/customers/*',
          element: (
            <S>
              <HelpCenterCustomersRoutes />
            </S>
          ),
        },
        {
          path: '/help-center/subcontractors/*',
          element: (
            <S>
              <HelpCenterContractorRoutes />
            </S>
          ),
        },
        {
          path: '/trades',
          element: (
            <S>
              <TradesPage />
            </S>
          ),
        },
        {
          path: '/vendor-list/:id',
          element: (
            <S>
              <VendorListPage />
            </S>
          ),
        },
        {
          path: '/projects/shared/:share_id',
          element: (
            <S>
              <EstimateReviewPage />
            </S>
          ),
        },
        {
          path: '/terms',
          element: (
            <S>
              <TermsAndConditionsPage />
            </S>
          ),
        },
        {
          path: '/privacy',
          element: (
            <S>
              <PrivacyPolicyPage />
            </S>
          ),
        },
        {
          path: '/unsubscribe',
          element: (
            <S>
              <UnsubscribePage />
            </S>
          ),
        },
        {
          path: '*',
          element: (
            <S>
              <NotFoundPage />
            </S>
          ),
        },
      ],
    },
  ],
};

const registrationRoutes = {
  children: [
    {
      path: '/join',
      element: (
        <S>
          <SignUpPage />
        </S>
      ),
    },
    {
      path: '/customer/sign-up',
      element: (
        <S>
          <CustomerCreateAccountPage />
        </S>
      ),
    },
    {
      path: '/customer/sign-up/completed',
      element: (
        <S>
          <CustomerAccountCreatedPage />
        </S>
      ),
    },
    {
      path: '/customers/request-estimate/:id',
      element: (
        <S>
          <RequestEstimatePage />
        </S>
      ),
    },
    {
      path: '/customers/request-estimate/success',
      element: (
        <S>
          <RequestEstimateSuccessPage />
        </S>
      ),
    },
    {
      element: <RegistrationRouteWrapper />,
      children: [
        {
          path: '/pro/verify-company-info',
          element: (
            <S>
              <VerifyCompanyInfoPage />
            </S>
          ),
        },
        {
          path: '/pro/verify-company-info/completed/:id',
          element: (
            <S>
              <VerifyCompanyInformationCompletedPage />
            </S>
          ),
        },
      ],
    },
    {
      path: '/pro/create-password',
      element: (
        <S>
          <CreatePasswordPage />
        </S>
      ),
    },
    {
      path: '/subcontractor/sign-up',
      element: (
        <S>
          <SubcontractorCreateAccountPage />
        </S>
      ),
    },
    {
      path: '/subcontractor/registration',
      element: (
        <S>
          <SubcontractorRegistrationPage />
        </S>
      ),
    },
    {
      path: '/subcontractor/registration/completed',
      element: (
        <S>
          <SubcontractorAccountCreatedPage />
        </S>
      ),
    },
    {
      path: '/sign-up/verify-email',
      element: (
        <S>
          <VerifyEmailPage />
        </S>
      ),
    },
    {
      path: '/sign-up/verify-email/validate',
      element: (
        <S>
          <ValidateEmailPage />
        </S>
      ),
    },
  ],
};

export const router = createBrowserRouter([
  {
    element: (
      <ErrorBoundary
        fallback={({ error, resetError }) => (
          <FallbackErrorPage
            resetErrorBoundary={resetError}
            error={error as Error}
          />
        )}
      >
        <App />
      </ErrorBoundary>
    ),
    children: [proRoutes, publicRoutes, registrationRoutes],
  },
]);
